import { useMemo, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpRight,
  Clock3,
  Plus,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Freshness, Lead, LeadDirection } from "./types";
import {
  availabilityLabels,
  directionLabels,
  exportLeadsCsv,
  formatDateTime,
  formatPrice,
  formatQuantity,
  freshnessLabels,
  getFreshness,
  leadSearchText,
  relativeTime,
  statusLabels,
} from "./utils";

const filterClass =
  "h-10 rounded-md border border-input bg-background px-3 text-sm shadow-none outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40";

export function FreshnessBadge({ lead }: { lead: Lead }) {
  const freshness = getFreshness(lead);
  const styles: Record<Freshness, string> = {
    fresh: "bg-emerald-400/10 text-emerald-300",
    expiring: "bg-amber-400/10 text-amber-300",
    expired: "bg-red-400/10 text-red-300",
    archived: "bg-secondary text-muted-foreground",
  };
  return (
    <Badge variant="outline" className={styles[freshness]}>
      <span className="size-1.5 rounded-full bg-current" />
      {freshnessLabels[freshness]}
    </Badge>
  );
}

export function LeadsView({
  leads,
  loading,
  onSelect,
  onNew,
  onAgent,
  contactFilter = null,
  onClearContactFilter,
}: {
  leads: Lead[];
  loading: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onAgent: () => void;
  contactFilter?: { id: string; name: string } | null;
  onClearContactFilter?: () => void;
}) {
  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState<"all" | LeadDirection>("all");
  const [scope, setScope] = useState("current");
  const [sort, setSort] = useState("updated");

  const visibleLeads = useMemo(() => {
    const query = search.trim().toLowerCase();
    return leads
      .filter((lead) => direction === "all" || lead.direction === direction)
      .filter((lead) => !contactFilter || lead.contact_id === contactFilter.id)
      .filter((lead) => {
        const freshness = getFreshness(lead);
        if (scope === "current")
          return freshness === "fresh" || freshness === "expiring";
        if (scope === "all") return true;
        return freshness === scope;
      })
      .filter((lead) => !query || leadSearchText(lead).includes(query))
      .sort((a, b) => {
        if (sort === "valid") return a.valid_until.localeCompare(b.valid_until);
        if (sort === "price") return (b.price ?? -1) - (a.price ?? -1);
        return b.updated_at.localeCompare(a.updated_at);
      });
  }, [leads, search, direction, scope, sort, contactFilter]);

  const resetFilters = () => {
    setSearch("");
    setDirection("all");
    setScope("current");
    onClearContactFilter?.();
  };
  const hasFilters =
    Boolean(search) ||
    direction !== "all" ||
    scope !== "current" ||
    Boolean(contactFilter);

  return (
    <>
      <section className="flex items-center justify-between gap-4">
        <h1 className="text-lg font-semibold">交易线索</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={onNew}
            aria-label="手工新增"
            title="手工新增"
          >
            <Plus />
          </Button>
          <Button
            size="icon"
            onClick={onAgent}
            aria-label="Agent 整理"
            title="Agent 整理"
          >
            <Sparkles />
          </Button>
        </div>
      </section>

      <section
        aria-labelledby="lead-table-title"
        className="mt-4 overflow-hidden rounded-lg border bg-card"
      >
        <div className="flex flex-col gap-3 border-b px-3 py-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center justify-between gap-3">
            <h2
              id="lead-table-title"
              className="px-1 text-xs font-medium text-muted-foreground"
            >
              {visibleLeads.length} 条盘口
            </h2>
            {contactFilter && (
              <Badge variant="secondary" className="gap-1.5">
                货主 · {contactFilter.name}
                <button
                  type="button"
                  aria-label="清除货主筛选"
                  title="清除货主筛选"
                  className="ml-0.5 inline-flex size-4 items-center justify-center rounded-full hover:bg-background"
                  onClick={() => onClearContactFilter?.()}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                exportLeadsCsv(visibleLeads);
                toast.success(`已导出 ${visibleLeads.length} 条盘口`);
              }}
              aria-label="导出"
              title="导出 CSV"
            >
              <ArrowDownToLine />
            </Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="搜索盘口…"
                className="h-10 border-0 bg-muted/60 pl-9 shadow-none"
                aria-label="搜索盘口"
              />
              {search && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  className="absolute top-1/2 right-2 grid size-7 -translate-y-1/2 place-items-center rounded hover:bg-muted"
                  onClick={() => setSearch("")}
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
            <div className="flex h-10 items-center rounded-md bg-muted/60 p-1">
              {(["all", "sell", "buy"] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setDirection(item)}
                  className={`h-8 flex-1 rounded px-3 text-xs font-medium whitespace-nowrap transition sm:flex-none ${direction === item ? "bg-background shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {item === "all" ? "全部" : directionLabels[item]}
                </button>
              ))}
            </div>
            <select
              className={filterClass}
              value={scope}
              onChange={(event) => setScope(event.target.value)}
              aria-label="时效筛选"
            >
              <option value="current">当前有效</option>
              <option value="expiring">临期 / 待核验</option>
              <option value="expired">已过期</option>
              <option value="archived">已归档</option>
              <option value="all">全部状态</option>
            </select>
            <select
              className={filterClass}
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              aria-label="排序方式"
            >
              <option value="updated">最近更新</option>
              <option value="valid">有效期最近</option>
              <option value="price">价格从高到低</option>
            </select>
            {hasFilters && (
              <Button
                variant="ghost"
                size="icon"
                onClick={resetFilters}
                aria-label="重置"
                title="重置筛选"
              >
                <X />
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid min-h-64 place-items-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock3 className="size-4 animate-spin" />
              正在读取盘口…
            </div>
          </div>
        ) : visibleLeads.length === 0 ? (
          <div className="grid min-h-72 place-items-center p-6 text-center">
            <div>
              <span className="mx-auto grid size-11 place-items-center rounded-full bg-muted">
                <Search className="size-5 text-muted-foreground" />
              </span>
              <h3 className="mt-4 text-sm font-medium">没有找到匹配盘口</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                调整筛选，或让 Agent 整理一段新消息。
              </p>
              <div className="mt-5 flex justify-center gap-2">
                {hasFilters && (
                  <Button variant="outline" onClick={resetFilters}>
                    清除筛选
                  </Button>
                )}
                <Button onClick={onAgent}>
                  <Sparkles />
                  打开 Agent
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b bg-muted/35 text-xs text-muted-foreground">
                    <th className="px-4 py-3 font-medium">方向 / 状态</th>
                    <th className="px-4 py-3 font-medium">型号与配置</th>
                    <th className="px-4 py-3 font-medium">数量</th>
                    <th className="px-4 py-3 font-medium">单价 / 口径</th>
                    <th className="px-4 py-3 font-medium">交割</th>
                    <th className="px-4 py-3 font-medium">对手方</th>
                    <th className="px-4 py-3 font-medium">时效</th>
                    <th className="px-4 py-3 text-right font-medium">更新</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="group border-b last:border-0 hover:bg-muted/25"
                    >
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col items-start gap-1.5">
                          <Badge
                            className={
                              lead.direction === "sell"
                                ? "bg-emerald-400/10 text-emerald-300"
                                : "bg-sky-400/10 text-sky-300"
                            }
                          >
                            {directionLabels[lead.direction]}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {statusLabels[lead.status]} ·{" "}
                            {availabilityLabels[lead.availability]}
                          </span>
                        </div>
                      </td>
                      <td className="max-w-72 px-4 py-4 align-top">
                        <button
                          type="button"
                          className="text-left outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => onSelect(lead.id)}
                        >
                          <span className="block truncate font-medium group-hover:text-primary">
                            {lead.model}
                          </span>
                          <span className="mt-1 block truncate text-xs text-muted-foreground">
                            {[lead.brand || lead.category, lead.configuration]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-4 align-top whitespace-nowrap">
                        {formatQuantity(lead)}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span className="block font-medium whitespace-nowrap">
                          {formatPrice(lead)}
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {lead.price_basis || "口径待确认"}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span className="block whitespace-nowrap">
                          {lead.location || "地点待确认"}
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {lead.available_at
                            ? formatDateTime(lead.available_at)
                            : "时间待确认"}
                        </span>
                      </td>
                      <td className="max-w-40 px-4 py-4 align-top">
                        <span className="block truncate">
                          {lead.company || lead.contact_name || "待确认"}
                        </span>
                        <span className="mt-1 block truncate text-xs text-muted-foreground">
                          {lead.contact_name || lead.source_channel}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <FreshnessBadge lead={lead} />
                        <span className="mt-1.5 block text-xs whitespace-nowrap text-muted-foreground">
                          至 {formatDateTime(lead.valid_until)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right align-top whitespace-nowrap">
                        <button
                          type="button"
                          className="inline-flex min-h-11 items-center gap-1 text-xs text-muted-foreground hover:text-foreground md:min-h-0"
                          onClick={() => onSelect(lead.id)}
                        >
                          {relativeTime(lead.updated_at)}
                          <ArrowUpRight className="size-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="divide-y md:hidden">
              {visibleLeads.map((lead) => (
                <button
                  type="button"
                  key={lead.id}
                  className="block w-full p-4 text-left active:bg-muted/40"
                  onClick={() => onSelect(lead.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex gap-2">
                      <Badge
                        className={
                          lead.direction === "sell"
                            ? "bg-emerald-400/10 text-emerald-300"
                            : "bg-sky-400/10 text-sky-300"
                        }
                      >
                        {directionLabels[lead.direction]}
                      </Badge>
                      <FreshnessBadge lead={lead} />
                    </div>
                    <ArrowUpRight className="size-4 text-muted-foreground" />
                  </div>
                  <h3 className="mt-3 font-medium">{lead.model}</h3>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {[lead.brand || lead.category, lead.configuration]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">数量</p>
                      <p className="mt-1">{formatQuantity(lead)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">单价</p>
                      <p className="mt-1 font-medium">{formatPrice(lead)}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{lead.location || "交割地待确认"}</span>
                    <span>至 {formatDateTime(lead.valid_until)}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
